import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router';
import { registrationService, bookmarkService, volunteerService, mediaUrl } from '../lib/api-service';
import { useAuth } from '../lib/auth';
import type { ActivityListItem, MyRegistration, MyVolunteerApplication } from '../lib/types';
import { formatDate, formatTime } from '../utils/date';
import { Calendar, Clock, MapPin, ArrowLeft, Loader2, CalendarX2, Heart, HandHelping } from 'lucide-react';

type Tab = 'future' | 'past' | 'bookmarks' | 'volunteering';

export default function MyEvents() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [registrations, setRegistrations] = useState<MyRegistration[]>([]);
  const [bookmarks, setBookmarks] = useState<ActivityListItem[]>([]);
  const [volunteerApps, setVolunteerApps] = useState<MyVolunteerApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>('future');

  useEffect(() => {
    if (!user) return;
    setLoading(true);

    if (tab === 'volunteering') {
      volunteerService
        .myApplications()
        .then((res) => setVolunteerApps(res.results))
        .catch(() => setVolunteerApps([]))
        .finally(() => setLoading(false));
    } else if (tab === 'bookmarks') {
      bookmarkService
        .myBookmarks()
        .then(setBookmarks)
        .catch(() => setBookmarks([]))
        .finally(() => setLoading(false));
    } else {
      registrationService
        .myRegistrations(tab === 'future')
        .then(setRegistrations)
        .catch(() => setRegistrations([]))
        .finally(() => setLoading(false));
    }
  }, [user, tab]);

  if (authLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--vivid-purple)]" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/cont" replace />;
  }

  const tabs: { key: Tab; label: string }[] = [
    { key: 'future', label: 'Viitoare' },
    { key: 'past', label: 'Trecute' },
    { key: 'volunteering', label: 'Voluntariat' },
    { key: 'bookmarks', label: 'Favorite' },
  ];

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      {/* Header */}
      <div className="mb-6 flex items-center gap-3">
        <Link to="/cont" className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-gray-100">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'var(--font-primary)' }}>
          Evenimentele mele
        </h1>
      </div>

      {/* Tab toggle */}
      <div className="mb-6 flex rounded-lg border bg-white p-1">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              tab === t.key
                ? 'bg-[var(--vivid-purple)] text-white'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            style={{ fontFamily: 'var(--font-primary)' }}
          >
            {t.key === 'volunteering' && <HandHelping className="mr-1.5 inline-block h-3.5 w-3.5" />}
            {t.key === 'bookmarks' && <Heart className="mr-1.5 inline-block h-3.5 w-3.5" />}
            {t.label}
          </button>
        ))}
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex min-h-[30vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-[var(--vivid-purple)]" />
        </div>
      )}

      {/* Volunteering tab */}
      {!loading && tab === 'volunteering' && (
        <>
          {volunteerApps.length === 0 ? (
            <div className="py-16 text-center">
              <HandHelping className="mx-auto mb-4 h-12 w-12 text-muted-foreground/50" />
              <p className="mb-2 text-lg font-semibold text-muted-foreground" style={{ fontFamily: 'var(--font-primary)' }}>
                Nu ai aplicat ca voluntar
              </p>
              <p className="mb-6 text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
                Explorează evenimentele deschise pentru voluntari
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Approved first, then pending */}
              {[...volunteerApps]
                .sort((a, b) => {
                  const order: Record<string, number> = { approved: 0, pending: 1, rejected: 2 };
                  return (order[a.status] ?? 3) - (order[b.status] ?? 3);
                })
                .map((app) => (
                  <Link
                    key={app.id}
                    to={`/eveniment/${app.activity_slug}`}
                    className="block overflow-hidden rounded-xl border bg-white shadow-sm transition-all hover:shadow-md"
                  >
                    <div className="flex">
                      <div className="hidden h-32 w-32 flex-shrink-0 sm:block">
                        {app.activity_banner ? (
                          <img src={mediaUrl(app.activity_banner)} alt="" className="h-full w-full object-cover" />
                        ) : (
                          <div className="h-full w-full bg-gradient-to-br from-[var(--vivid-purple)]/20 to-[var(--vivid-cyan)]/10" />
                        )}
                      </div>
                      <div className="flex flex-1 flex-col justify-between p-4">
                        <div>
                          <div className="mb-2 flex items-start justify-between gap-3">
                            <h3 className="line-clamp-2 text-base font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
                              {app.activity_title}
                            </h3>
                            <span
                              className={`flex-shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${
                                app.status === 'approved'
                                  ? 'bg-green-100 text-green-700'
                                  : app.status === 'pending'
                                  ? 'bg-amber-100 text-amber-700'
                                  : 'bg-red-100 text-red-600'
                              }`}
                              style={{ fontFamily: 'var(--font-primary)' }}
                            >
                              {app.status === 'approved' ? 'Aprobat' : app.status === 'pending' ? 'În așteptare' : 'Respins'}
                            </span>
                          </div>
                          <div className="space-y-1 text-xs text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
                            <div className="flex items-center gap-2">
                              <Calendar className="h-3.5 w-3.5 text-[var(--vivid-purple)]" />
                              <span>{formatDate(app.activity_start)}</span>
                              <Clock className="ml-1 h-3.5 w-3.5 text-[var(--vivid-purple)]" />
                              <span>{formatTime(app.activity_start)}</span>
                            </div>
                            {app.activity_location && (
                              <div className="flex items-center gap-2">
                                <MapPin className="h-3.5 w-3.5 text-[var(--vivid-purple)]" />
                                <span className="line-clamp-1">{app.activity_location}</span>
                              </div>
                            )}
                            {app.team_name && (
                              <div className="flex items-center gap-2">
                                <HandHelping className="h-3.5 w-3.5 text-[var(--vivid-purple)]" />
                                <span>{app.team_name}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
            </div>
          )}
        </>
      )}

      {/* Bookmarks tab */}
      {!loading && tab === 'bookmarks' && (
        <>
          {bookmarks.length === 0 ? (
            <div className="py-16 text-center">
              <Heart className="mx-auto mb-4 h-12 w-12 text-muted-foreground/50" />
              <p className="mb-2 text-lg font-semibold text-muted-foreground" style={{ fontFamily: 'var(--font-primary)' }}>
                Nu ai evenimente favorite
              </p>
              <p className="mb-6 text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
                Apasă pe inima de pe pagina unui eveniment pentru a-l salva
              </p>
              <Link
                to="/evenimente"
                className="inline-block rounded-lg bg-[var(--vivid-purple)] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[var(--vivid-purple)]/90"
                style={{ fontFamily: 'var(--font-primary)' }}
              >
                Explorează evenimente
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {bookmarks.map((a) => (
                <button
                  key={a.id}
                  onClick={() => navigate(`/eveniment/${a.slug}`)}
                  className="block w-full overflow-hidden rounded-xl border bg-white text-left shadow-sm transition-all hover:shadow-md"
                >
                  <div className="flex">
                    <div className="hidden h-32 w-32 flex-shrink-0 sm:block">
                      {a.banner_image ? (
                        <img src={mediaUrl(a.banner_image)} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <div className="h-full w-full bg-gradient-to-br from-[var(--vivid-purple)]/20 to-[var(--vivid-cyan)]/10" />
                      )}
                    </div>
                    <div className="flex flex-1 flex-col justify-between p-4">
                      <h3 className="mb-2 line-clamp-2 text-base font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
                        {a.title}
                      </h3>
                      <div className="space-y-1 text-xs text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-3.5 w-3.5 text-[var(--vivid-purple)]" />
                          <span>{formatDate(a.start_datetime)}</span>
                          <Clock className="ml-1 h-3.5 w-3.5 text-[var(--vivid-purple)]" />
                          <span>{formatTime(a.start_datetime)}</span>
                        </div>
                        {a.location_text && (
                          <div className="flex items-center gap-2">
                            <MapPin className="h-3.5 w-3.5 text-[var(--vivid-purple)]" />
                            <span className="line-clamp-1">{a.location_text}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {/* Registrations tabs (future/past) */}
      {!loading && (tab === 'future' || tab === 'past') && (
        <>
          {registrations.length === 0 ? (
            <div className="py-16 text-center">
              <CalendarX2 className="mx-auto mb-4 h-12 w-12 text-muted-foreground/50" />
              <p className="mb-2 text-lg font-semibold text-muted-foreground" style={{ fontFamily: 'var(--font-primary)' }}>
                Nu ai nicio activitate înregistrată
              </p>
              <p className="mb-6 text-sm text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
                Descoperă evenimentele disponibile și înscrie-te
              </p>
              <Link
                to="/evenimente"
                className="inline-block rounded-lg bg-[var(--vivid-purple)] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[var(--vivid-purple)]/90"
                style={{ fontFamily: 'var(--font-primary)' }}
              >
                Explorează evenimente
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {registrations.map((reg) => (
                <Link
                  key={reg.id}
                  to={`/eveniment/${reg.activity.slug}`}
                  className="block overflow-hidden rounded-xl border bg-white shadow-sm transition-all hover:shadow-md"
                >
                  <div className="flex">
                    <div className="hidden h-32 w-32 flex-shrink-0 sm:block">
                      {reg.activity.banner_image ? (
                        <img src={mediaUrl(reg.activity.banner_image)} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <div className="h-full w-full bg-gradient-to-br from-[var(--vivid-purple)]/20 to-[var(--vivid-cyan)]/10" />
                      )}
                    </div>
                    <div className="flex flex-1 flex-col justify-between p-4">
                      <div>
                        <div className="mb-2 flex items-start justify-between gap-3">
                          <h3 className="line-clamp-2 text-base font-semibold" style={{ fontFamily: 'var(--font-primary)' }}>
                            {reg.activity.title}
                          </h3>
                          <span
                            className={`flex-shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${
                              reg.status === 'confirmed' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                            }`}
                            style={{ fontFamily: 'var(--font-primary)' }}
                          >
                            {reg.status === 'confirmed' ? 'Confirmat' : 'Anulat'}
                          </span>
                        </div>
                        <div className="space-y-1 text-xs text-muted-foreground" style={{ fontFamily: 'var(--font-secondary)' }}>
                          <div className="flex items-center gap-2">
                            <Calendar className="h-3.5 w-3.5 text-[var(--vivid-purple)]" />
                            <span>{formatDate(reg.activity.start_datetime)}</span>
                            <Clock className="ml-1 h-3.5 w-3.5 text-[var(--vivid-purple)]" />
                            <span>{formatTime(reg.activity.start_datetime)}</span>
                          </div>
                          {reg.activity.location_text && (
                            <div className="flex items-center gap-2">
                              <MapPin className="h-3.5 w-3.5 text-[var(--vivid-purple)]" />
                              <span className="line-clamp-1">{reg.activity.location_text}</span>
                            </div>
                          )}
                          {reg.participant_type && (
                            <div className="flex items-center gap-2">
                              <span className="rounded-full bg-[var(--vivid-purple)]/10 px-2 py-0.5 text-[10px] font-semibold text-[var(--vivid-purple)]">
                                {reg.participant_type.title}
                                {parseFloat(reg.participant_type.price) > 0 && ` · ${parseFloat(reg.participant_type.price)} RON`}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
